import { ValidationSchema } from 'forms';
import {
  PERSONAL_PAN_REGEX,
  AADHAAR_REGEX,
  PF_UAN_REGEX,
  ESIC_IP_NUMBER_REGEX,
  PHONE_NUMBER_REGEX,
  EMAIL_REGEX,
} from 'utils/regexes';
import {
  KycFormFields,
  StatutoryFormFields,
  PersonalDetailsFormFields,
  GenderOption,
  FormFields,
} from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/types';
import { genderOptions } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/constants';
import { statesOfIndia } from 'utils/StatesOfIndia';
import { StepItemProps } from '@razorpay/blade/components';

const EMAIL_PATTERN = new RegExp(EMAIL_REGEX);
const PHONE_NUMBER_PATTERN = new RegExp(PHONE_NUMBER_REGEX);

export const validatePAN = (pan: string): boolean => {
  return PERSONAL_PAN_REGEX.test(pan.toUpperCase());
};

export const validateAadhar = (aadhar: string): boolean => {
  const cleanAadhar = aadhar.replace(/\s/g, '');
  return AADHAAR_REGEX.test(cleanAadhar);
};

export const validatePfUan = (uan: string): boolean => {
  return PF_UAN_REGEX.test(uan.replace(/\s/g, ''));
};

export const validateEsicIpNumber = (ipNumber: string): boolean => {
  return ESIC_IP_NUMBER_REGEX.test(ipNumber.replace(/\s/g, ''));
};

export const validatePhoneNumber = (phoneNumber: string): boolean => {
  return PHONE_NUMBER_PATTERN.test(phoneNumber.replace(/\s/g, ''));
};

export const validateEmail = (email: string): boolean => {
  return EMAIL_PATTERN.test(email);
};

export const validateDateOfBirth = (date: Date | null): boolean => {
  if (!date) return false;
  const today = new Date();
  return date <= today;
};

export const getKycValidationSchema = (isTdsEnabled: boolean): ValidationSchema<KycFormFields> => ({
  pan: (formData) => {
    if (isTdsEnabled) {
      if (!formData.pan?.trim()) {
        return 'PAN is required';
      }
      if (!validatePAN(formData.pan)) {
        return 'Please enter a valid PAN (e.g., ABCPE1234F)';
      }
    }
    return null;
  },
  aadharNumber: (formData) => {
    if (formData.aadharNumber) {
      if (!validateAadhar(formData.aadharNumber)) {
        return 'Please enter a valid 12-digit Aadhar number';
      }
    }
    return null;
  },
  verificationConsent: (formData) =>
    !formData.verificationConsent ? 'Please provide consent for validation' : null,
});

export const getStatutoryValidationSchema = ({
  isPfEnabled,
  isEsicEnabled,
}: {
  isPfEnabled: boolean;
  isEsicEnabled: boolean;
}): ValidationSchema<StatutoryFormFields> => ({
  pfUan: (formData) => {
    if (formData.doNotHavePfUan || !isPfEnabled) {
      return null;
    }
    if (!formData.pfUan?.trim()) {
      return 'PF UAN is required';
    }
    if (!validatePfUan(formData.pfUan)) {
      return 'Please enter a valid 12-digit PF UAN';
    }
    return null;
  },
  esicIpNumber: (formData) => {
    if (formData.doNotHaveEsicIpNumber || !isEsicEnabled) {
      return null;
    }
    if (!formData.esicIpNumber?.trim()) {
      return 'ESIC IP Number is required';
    }
    if (!validateEsicIpNumber(formData.esicIpNumber)) {
      return 'Please enter a valid 10-digit ESIC IP Number';
    }
    return null;
  },
});

export const getPersonalDetailsValidationSchema =
  (): ValidationSchema<PersonalDetailsFormFields> => ({
    gender: (formData) => (!formData.gender?.[0] ? 'Gender is required' : null),
    dob: (formData) => {
      if (!formData.dob) {
        return 'Date of birth is required';
      }
      if (!validateDateOfBirth(formData.dob)) {
        return 'Please enter a valid date of birth';
      }
      return null;
    },
    personalEmail: (formData) => {
      if (formData.personalEmail?.trim() && !validateEmail(formData.personalEmail)) {
        return 'Please enter a valid email address';
      }
      return null;
    },
    phoneNumber: (formData) => {
      if (formData.phoneNumber && !validatePhoneNumber(formData.phoneNumber)) {
        return 'Please enter a valid phone number';
      }
      return null;
    },
  });

export const getStepProgress = (
  currentStep: number,
  stepNumber: number,
): StepItemProps['stepProgress'] => {
  if (stepNumber < currentStep) return 'full';
  if (stepNumber === currentStep) return 'start';
  return 'none';
};

export type StepMarkerType = {
  type: 'icon' | 'indicator';
  color: 'positive' | 'primary' | 'neutral';
};

export const getStepMarker = (currentStep: number, stepNumber: number): StepMarkerType => {
  if (stepNumber < currentStep) {
    return { type: 'icon', color: 'positive' };
  }
  if (stepNumber === currentStep) {
    return { type: 'indicator', color: 'primary' };
  }
  return { type: 'indicator', color: 'neutral' };
};

export const getStateName = (stateCode?: number): string => {
  if (!stateCode) return '';
  return statesOfIndia.find((state) => state.code === stateCode)?.name || '';
};

export const getGenderTitle = (value?: GenderOption['value']): string => {
  if (!value) return '';
  return genderOptions.find((option) => option.value === value)?.title || '';
};

export const reviewDetailsValidationSchema: ValidationSchema<FormFields> = {
  ...getKycValidationSchema(true),
  ...getStatutoryValidationSchema({
    isPfEnabled: false, //as PF UAN is not mandatory for self onboarding
    isEsicEnabled: false, //as ESIC IP is not mandatory for self onboarding
  }),
  ...getPersonalDetailsValidationSchema(),
};
